import { useEffect, type RefObject } from "react";

// Looping portrait videos (the hero character, the tablet operator, the
// connect-form porthole). Posters show first; only the most visible loop
// plays, and only while the tab is visible and motion is allowed; every other
// loop stays paused (prototype/home/v1). A box's active video is its selected
// look (".look.on video") or its only video. The motion preference is read
// live: turning reduced motion on while the page is open pauses every loop at
// once, turning it off again resumes the most visible one.
//
// Safari leaves a playing loop paused or frozen on a frame by itself after a
// while (a media interruption, power saving, a loop that never restarts), with
// no event this module would hear. While a loop should play, a check every two
// seconds starts it again when it is paused and restarts it when it stopped
// advancing although nothing is loading: first from the beginning, then by
// reloading the file. A browser that refuses to play without a person's
// gesture (Safari in Low Power Mode) gets another try on the next press.

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const CHECK_MS = 2000;
// HTMLMediaElement.NETWORK_LOADING: the browser is fetching media data.
const NETWORK_LOADING = 2;

const boxes: HTMLElement[] = [];
const seen = new Map<HTMLElement, number>();
let observer: IntersectionObserver | null = null;
let motion: MediaQueryList | null = null;
// The loop that should be playing now, and what the check last saw of it.
let expected: HTMLVideoElement | null = null;
let checker: ReturnType<typeof setInterval> | null = null;
let lastTime = -1;
let stalls = 0;
let awaitingGesture = false;

function activeVideo(box: HTMLElement): HTMLVideoElement | null {
  return box.querySelector<HTMLVideoElement>(".look.on video") ?? box.querySelector<HTMLVideoElement>("video");
}

function onGesture(): void {
  stopAwaitingGesture();
  refreshLoopVideos();
}

function stopAwaitingGesture(): void {
  if (!awaitingGesture) return;
  awaitingGesture = false;
  document.removeEventListener("pointerdown", onGesture, true);
  document.removeEventListener("keydown", onGesture, true);
}

function play(video: HTMLVideoElement): void {
  video.play().catch((error: unknown) => {
    if (awaitingGesture || !(error instanceof DOMException) || error.name !== "NotAllowedError") return;
    awaitingGesture = true;
    document.addEventListener("pointerdown", onGesture, true);
    document.addEventListener("keydown", onGesture, true);
  });
}

function checkExpected(): void {
  const video = expected;
  if (!video) return;
  if (video.paused) {
    play(video);
    return;
  }
  if (video.currentTime !== lastTime || video.networkState === NETWORK_LOADING) {
    lastTime = video.currentTime;
    stalls = 0;
    return;
  }
  stalls += 1;
  if (stalls === 1) {
    video.currentTime = 0;
  } else {
    video.load();
    stalls = 0;
  }
  lastTime = video.currentTime;
  play(video);
}

function expect(video: HTMLVideoElement | null): void {
  if (video !== expected) {
    expected = video;
    lastTime = -1;
    stalls = 0;
  }
  if (expected && checker === null) checker = setInterval(checkExpected, CHECK_MS);
  if (!expected && checker !== null) {
    clearInterval(checker);
    checker = null;
  }
}

/** Plays the active video of the most visible box (at least 20% in view) and pauses every other video. */
export function refreshLoopVideos(): void {
  if (typeof document === "undefined") return;
  let best: HTMLElement | null = null;
  let ratio = 0;
  if (!motion?.matches && !document.hidden) {
    seen.forEach((visible, box) => {
      if (visible > ratio) {
        ratio = visible;
        best = box;
      }
    });
  }
  if (ratio < 0.2) best = null;
  let playing: HTMLVideoElement | null = null;
  for (const box of boxes) {
    const active = activeVideo(box);
    box.querySelectorAll("video").forEach((video) => {
      if (video === active && box === best) {
        video.muted = true;
        if (video.paused) play(video);
        playing = video;
      } else if (!video.paused) {
        video.pause();
      }
    });
  }
  expect(playing);
}

function ensureObserver(): IntersectionObserver | null {
  if (observer || typeof window === "undefined" || !("IntersectionObserver" in window)) return observer;
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) seen.set(entry.target as HTMLElement, entry.isIntersecting ? entry.intersectionRatio : 0);
    refreshLoopVideos();
  }, { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] });
  motion = window.matchMedia(REDUCED_MOTION);
  motion.addEventListener("change", refreshLoopVideos);
  document.addEventListener("visibilitychange", refreshLoopVideos);
  return observer;
}

// With the last box gone, no observer, timer or listener outlives the loops.
function releaseObserver(): void {
  observer?.disconnect();
  observer = null;
  motion?.removeEventListener("change", refreshLoopVideos);
  motion = null;
  document.removeEventListener("visibilitychange", refreshLoopVideos);
  expect(null);
  stopAwaitingGesture();
}

/** Registers a looping-video box until the returned function is called; does nothing without IntersectionObserver. */
export function registerLoopVideoBox(box: HTMLElement): () => void {
  const io = ensureObserver();
  if (!io) return () => {};
  boxes.push(box);
  seen.set(box, 0);
  io.observe(box);
  return () => {
    io.unobserve(box);
    seen.delete(box);
    boxes.splice(boxes.indexOf(box), 1);
    if (boxes.length === 0) releaseObserver();
    else refreshLoopVideos();
  };
}

/** Registers a looping-video box for as long as the component is mounted. */
export function useLoopVideoBox(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    return registerLoopVideoBox(box);
  }, [ref]);
}
